/**
 * 画像最適化ユーティリティ
 * アップロード前の画像圧縮とリサイズ処理
 */

export interface ImageOptimizationConfig {
  maxWidth: number;
  maxHeight: number;
  quality: number;
  format: string;
  progressive: boolean;
}

export interface OptimizationResult {
  originalSize: number;
  optimizedSize: number;
  compressionRatio: number;
  optimizedBlob: Blob;
  width: number;
  height: number;
}

export class ImageOptimizer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  constructor() {
    this.canvas = document.createElement('canvas');
    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas 2D context not supported');
    }
    this.ctx = ctx;
  }

  /**
   * 画像の最適化処理
   */
  async optimize(blob: Blob, config: ImageOptimizationConfig): Promise<OptimizationResult> {
    const originalSize = blob.size;

    // 画像をImageオブジェクトとして読み込み
    const img = await this.loadImage(blob);

    // リサイズ計算
    const { width, height } = this.calculateOptimalSize(
      img.width,
      img.height,
      config.maxWidth,
      config.maxHeight
    );

    // Canvas設定
    this.canvas.width = width;
    this.canvas.height = height;

    // 高品質レンダリング設定
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = 'high';

    // 画像描画
    this.ctx.drawImage(img, 0, 0, width, height);

    // 最適化されたBlobを生成
    const optimizedBlob = await this.canvasToBlob(config);
    const optimizedSize = optimizedBlob.size;
    const compressionRatio = ((originalSize - optimizedSize) / originalSize) * 100;

    return {
      originalSize,
      optimizedSize,
      compressionRatio,
      optimizedBlob,
      width,
      height,
    };
  }

  /**
   * 複数画像の並列最適化
   */
  async optimizeBatch(
    blobs: Blob[],
    config: ImageOptimizationConfig
  ): Promise<OptimizationResult[]> {
    console.log(`🖼️ 画像バッチ最適化開始: ${blobs.length}枚`);

    const startTime = Date.now();
    const results = await Promise.all(
      blobs.map(async (blob, index) => {
        try {
          const result = await this.optimize(blob, config);
          console.log(
            `📸 画像${index + 1}最適化完了: ${this.formatBytes(result.originalSize)} → ${this.formatBytes(result.optimizedSize)} (${result.compressionRatio.toFixed(1)}%削減)`
          );
          return result;
        } catch (error) {
          console.error(`❌ 画像${index + 1}最適化失敗:`, error);
          throw error;
        }
      })
    );

    const duration = Date.now() - startTime;
    const totalOriginalSize = results.reduce((sum, r) => sum + r.originalSize, 0);
    const totalOptimizedSize = results.reduce((sum, r) => sum + r.optimizedSize, 0);
    const totalCompressionRatio =
      ((totalOriginalSize - totalOptimizedSize) / totalOriginalSize) * 100;

    console.log(
      `✅ バッチ最適化完了: ${duration}ms, 総削減率: ${totalCompressionRatio.toFixed(1)}% (${this.formatBytes(totalOriginalSize)} → ${this.formatBytes(totalOptimizedSize)})`
    );

    return results;
  }

  /**
   * BlobからImageオブジェクトを作成
   */
  private loadImage(blob: Blob): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }

  /**
   * 最適なサイズを計算（アスペクト比維持）
   */
  private calculateOptimalSize(
    originalWidth: number,
    originalHeight: number,
    maxWidth: number,
    maxHeight: number
  ): { width: number; height: number } {
    if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
      return { width: originalWidth, height: originalHeight };
    }

    const aspectRatio = originalWidth / originalHeight;

    if (originalWidth > originalHeight) {
      // 横長画像
      const width = Math.min(originalWidth, maxWidth);
      const height = width / aspectRatio;

      if (height > maxHeight) {
        return {
          width: maxHeight * aspectRatio,
          height: maxHeight,
        };
      }

      return { width, height };
    } else {
      // 縦長画像
      const height = Math.min(originalHeight, maxHeight);
      const width = height * aspectRatio;

      if (width > maxWidth) {
        return {
          width: maxWidth,
          height: maxWidth / aspectRatio,
        };
      }

      return { width, height };
    }
  }

  /**
   * CanvasからBlobを生成
   */
  private canvasToBlob(config: ImageOptimizationConfig): Promise<Blob> {
    return new Promise((resolve, reject) => {
      this.canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create optimized blob'));
          }
        },
        config.format,
        config.quality
      );
    });
  }

  /**
   * バイト数をフォーマット
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * リソース解放
   */
  dispose(): void {
    if (this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
  }
}

/**
 * デフォルト設定での画像最適化
 */
export const DEFAULT_OPTIMIZATION_CONFIG: ImageOptimizationConfig = {
  maxWidth: 640,
  maxHeight: 480,
  quality: 0.8,
  format: 'image/jpeg',
  progressive: true,
};

/**
 * 設定プリセット
 */
export const OptimizationPresets = {
  /**
   * 高品質（大きいファイルサイズ）
   */
  HIGH_QUALITY: {
    maxWidth: 1280,
    maxHeight: 720,
    quality: 0.9,
    format: 'image/jpeg',
    progressive: true,
  } as ImageOptimizationConfig,

  /**
   * 標準品質（バランス）
   */
  STANDARD: DEFAULT_OPTIMIZATION_CONFIG,

  /**
   * 低品質（小さいファイルサイズ）
   */
  LOW_QUALITY: {
    maxWidth: 480,
    maxHeight: 360,
    quality: 0.6,
    format: 'image/jpeg',
    progressive: false,
  } as ImageOptimizationConfig,

  /**
   * 面接用（スクリーンショット最適化）
   */
  INTERVIEW_SCREENSHOT: {
    maxWidth: 640,
    maxHeight: 480,
    quality: 0.75,
    format: 'image/jpeg',
    progressive: true,
  } as ImageOptimizationConfig,
};
